import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ArrowLeft, Send } from 'lucide-react';
import { useMessages, useSendMessage, useMarkMessagesAsRead, type Message } from '@/hooks/useMessages';
import { type Conversation } from '@/hooks/useConversations';
import { useAuth } from '@/contexts/AuthContext';
import { useBreederProfile } from '@/hooks/useBreederProfile';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';

interface MessageThreadProps {
  conversation: Conversation;
  onBack: () => void;
}

export const MessageThread = ({ conversation, onBack }: MessageThreadProps) => {
  const { user } = useAuth();
  const { data: messages = [], isLoading } = useMessages(conversation.id);
  const sendMessageMutation = useSendMessage();
  const markMessagesAsRead = useMarkMessagesAsRead();
  const [newMessage, setNewMessage] = useState('');
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const hasMarkedAsRead = useRef(false);

  const isUserBreeder = user?.user_metadata?.user_type === 'breeder';
  
  // Determine the other participant based on current user
  const otherParticipantId = user?.id === conversation.buyer_id ? conversation.breeder_id : conversation.buyer_id;
  
  // Fetch the other participant's profile to determine their actual type
  const { data: otherParticipantProfile } = useQuery({
    queryKey: ['participant-profile', otherParticipantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, profile_image_url, business_name, user_type')
        .eq('id', otherParticipantId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!otherParticipantId,
  });

  // Fetch breeder profile if the other participant is a breeder
  const { data: breederProfile } = useBreederProfile({ 
    breederId: otherParticipantId 
  });

  // Fetch current user's profile for avatar
  const { data: currentUserProfile } = useQuery({
    queryKey: ['current-user-profile', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, profile_image_url, business_name, user_type')
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  // Mark messages as read when component loads and messages are available
  useEffect(() => {
    if (messages.length > 0 && !hasMarkedAsRead.current) {
      const unreadMessages = messages.filter(
        (message: Message) => 
          message.sender_id !== user?.id && 
          message.read_at === null
      );
      
      if (unreadMessages.length > 0) {
        markMessagesAsRead.mutate(conversation.id);
        hasMarkedAsRead.current = true;
      }
    }
  }, [messages, user?.id, conversation.id]); // Removed markMessagesAsRead from dependencies

  // Auto scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    try {
      await sendMessageMutation.mutateAsync({
        conversation_id: conversation.id,
        content: newMessage.trim(),
      });
      setNewMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const getOtherParticipantName = () => {
    if (!otherParticipantProfile) return 'Loading...';
    
    if (otherParticipantProfile.user_type === 'buyer') {
      // Other participant is buyer - show "John D." format
      const firstName = otherParticipantProfile.first_name || '';
      const lastName = otherParticipantProfile.last_name || '';
      if (firstName && lastName) {
        return `${firstName} ${lastName.charAt(0)}.`;
      } else if (firstName) {
        return firstName;
      } else if (otherParticipantProfile.business_name) {
        return otherParticipantProfile.business_name;
      }
      return 'Unknown Buyer';
    } else if (otherParticipantProfile.user_type === 'breeder') {
      // Other participant is breeder - show business name
      return otherParticipantProfile.business_name || 
             `${otherParticipantProfile.first_name || ''} ${otherParticipantProfile.last_name || ''}`.trim() || 
             'Unknown Breeder';
    }
    
    return 'Unknown User';
  };

  const getOtherParticipantProfileUrl = () => {
    if (!otherParticipantProfile) return '#';
    
    if (otherParticipantProfile.user_type === 'buyer') {
      return `/buyer/${otherParticipantId}`;
    } else if (otherParticipantProfile.user_type === 'breeder') {
      return `/breeder/${otherParticipantId}`;
    }
    
    return '#';
  };

  const getCurrentUserName = () => {
    if (!currentUserProfile) return 'You';
    
    if (currentUserProfile.user_type === 'breeder') {
      // Current user is breeder
      return currentUserProfile.business_name || 'You';
    } else if (currentUserProfile.user_type === 'buyer') {
      // Current user is buyer - show "John D." format
      const firstName = currentUserProfile.first_name || '';
      const lastName = currentUserProfile.last_name || '';
      if (firstName && lastName) {
        return `${firstName} ${lastName.charAt(0)}.`;
      } else if (firstName) {
        return firstName;
      } else if (currentUserProfile.business_name) {
        return currentUserProfile.business_name;
      }
    }
    return 'You';
  };

  const getMessageSenderName = (message: Message) => {
    const isOwn = message.sender_id === user?.id;
    if (isOwn) {
      return getCurrentUserName();
    } else {
      return getOtherParticipantName();
    }
  };

  const getMessageSenderProfileUrl = (message: Message) => {
    const isOwn = message.sender_id === user?.id;
    if (isOwn) {
      // Link to current user's profile
      if (currentUserProfile?.user_type === 'breeder') {
        return `/breeder/${user?.id}`;
      } else {
        return `/buyer/${user?.id}`;
      }
    } else {
      return getOtherParticipantProfileUrl();
    }
  };

  const getAvatarData = (isOwn: boolean) => {
    if (isOwn) {
      // Current user's avatar
      const image = currentUserProfile?.profile_image_url;
      let fallback = '';
      
      if (currentUserProfile?.business_name) {
        fallback = currentUserProfile.business_name.charAt(0).toUpperCase();
      } else if (currentUserProfile?.first_name || currentUserProfile?.last_name) {
        const firstName = currentUserProfile.first_name?.charAt(0) || '';
        const lastName = currentUserProfile.last_name?.charAt(0) || '';
        fallback = `${firstName}${lastName}`.toUpperCase() || 'U';
      } else {
        fallback = 'U';
      }
      
      return { image, fallback };
    } else {
      // Other participant's avatar
      if (otherParticipantProfile) {
        const image = otherParticipantProfile.profile_image_url;
        let fallback = '';
        
        if (otherParticipantProfile.user_type === 'buyer') {
          if (otherParticipantProfile.first_name && otherParticipantProfile.last_name) {
            fallback = `${otherParticipantProfile.first_name.charAt(0)}${otherParticipantProfile.last_name.charAt(0)}`.toUpperCase();
          } else if (otherParticipantProfile.first_name) {
            fallback = otherParticipantProfile.first_name.charAt(0).toUpperCase();
          } else if (otherParticipantProfile.business_name) {
            fallback = otherParticipantProfile.business_name.charAt(0).toUpperCase();
          } else {
            fallback = 'B';
          }
        } else if (otherParticipantProfile.user_type === 'breeder') {
          fallback = otherParticipantProfile.business_name?.charAt(0).toUpperCase() || 
                    otherParticipantProfile.first_name?.charAt(0).toUpperCase() || 'BR';
        } else {
          fallback = 'U';
        }
        
        return { image, fallback };
      }
      
      return { image: undefined, fallback: 'U' };
    }
  };

  const otherParticipantName = getOtherParticipantName();
  const otherParticipantProfileUrl = getOtherParticipantProfileUrl();
  const listingTitle = conversation.listing_title || 'General Question';

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      <Card className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Header */}
        <CardHeader className="flex-shrink-0 pb-3 sm:pb-4 border-b border-gray-200">
          <div className="flex items-center gap-2 sm:gap-3">
            <Button 
              variant="outline" 
              size="icon"
              onClick={onBack} 
              className="bg-green-600 hover:bg-green-700 text-white border-green-600 hover:border-green-700 flex-shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex-1 min-w-0">
              <CardTitle className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
                {conversation.listing_title ? (
                  <Link 
                    to={conversation.listing_url}
                    className="hover:text-green-700 hover:underline transition-colors"
                  >
                    {listingTitle}
                  </Link>
                ) : (
                  <span>{listingTitle}</span>
                )}
              </CardTitle>
              <p className="text-sm text-gray-600">
                <Link 
                  to={otherParticipantProfileUrl}
                  className="hover:text-green-700 hover:underline transition-colors"
                >
                  {otherParticipantName}
                </Link>
              </p>
            </div>
          </div>
        </CardHeader>
        
        {/* Messages area - fills remaining space above input */}
        <CardContent className="flex-1 min-h-0 px-3 sm:px-6 pb-3 sm:pb-4 pt-3 overflow-hidden">
          <ScrollArea className="h-full pr-2 sm:pr-4" ref={scrollAreaRef}>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-700"></div>
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm sm:text-base">No messages yet. Start the conversation!</p>
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4 pb-2 sm:pb-4">
                {messages.map((message: Message) => {
                  const isOwn = message.sender_id === user?.id;
                  const avatarData = getAvatarData(isOwn);
                  const senderName = getMessageSenderName(message);
                  const senderProfileUrl = getMessageSenderProfileUrl(message);
                  
                  return (
                    <div key={message.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                      <div className={`flex flex-col max-w-[90%] sm:max-w-[85%] md:max-w-[70%] ${isOwn ? 'items-end' : 'items-start'}`}>
                        {/* Sender name as clickable link */}
                        <Link 
                          to={senderProfileUrl}
                          className="text-xs text-gray-500 hover:text-green-700 hover:underline mb-1 transition-colors"
                        >
                          {senderName}
                        </Link>
                        
                        {/* Message bubble */}
                        <div className={`flex items-start space-x-2 ${isOwn ? 'flex-row-reverse space-x-reverse' : ''}`}>
                          <Avatar className="w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0">
                            <AvatarImage src={avatarData.image} />
                            <AvatarFallback className="bg-green-700 text-white font-medium text-xs">
                              {avatarData.fallback}
                            </AvatarFallback>
                          </Avatar>
                          <Card className={`${
                            isOwn 
                              ? 'bg-green-600 text-white border-green-600' 
                              : 'bg-white text-gray-900 border-gray-200'
                          }`}>
                            <CardContent className="px-3 py-2 sm:px-4">
                              <p className="text-xs sm:text-sm break-words">{message.content}</p>
                              <p className={`text-xs mt-1 ${isOwn ? 'text-green-100' : 'text-gray-500'}`}>
                                {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                              </p>
                            </CardContent>
                          </Card>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </CardContent>
        
        {/* Message Input - Fixed at bottom of card */}
        <CardContent className="flex-shrink-0 pt-3 px-3 sm:px-6 pb-3 sm:pb-6 border-t border-gray-200">
          <form onSubmit={handleSendMessage}>
            <div className="flex items-center space-x-2">
              <Input
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                className="flex-1 text-sm sm:text-base"
                disabled={sendMessageMutation.isPending}
              />
              <Button 
                type="submit" 
                size="icon"
                className="bg-green-700 hover:bg-green-800 flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10"
                disabled={!newMessage.trim() || sendMessageMutation.isPending}
              >
                {sendMessageMutation.isPending ? (
                  <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white"></div>
                ) : (
                  <Send className="w-3 h-3 sm:w-4 sm:h-4" />
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
